/**
 * @file Pure page-analysis logic — zero DOM dependency.
 *
 * Discover pages from sitemap + nav links, merge/dedupe, derive names.
 * Used by screens/existing.js and build/page-analyzer.js.
 */
import type { DiscoveredPage, Section, SiteStructureSection } from '../types.ts';

// ── ASSET EXTENSIONS ─────────────────────────────────────────────────
const ASSET_EXT = /\.(jpg|jpeg|png|gif|svg|webp|pdf|xml|css|js|ico|woff2?|ttf|eot|mp4|mp3|zip)$/i;

// ── PAGE NAME DERIVATION ─────────────────────────────────────────────

/** Derive a display name from a URL pathname slug. */
export const derivePageName = (pathname: string): string => {
  const slug =
    pathname
      .replace(/^\/+|\/+$/g, '')
      .split('/')
      .pop() || '';
  if (!slug || slug === 'index' || slug === 'index.html') {
    return 'Home';
  }
  return slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// ── NAV LINK EXTRACTION ──────────────────────────────────────────────

interface RawNavLink {
  url: string;
  text: string;
}

/** Extract nav links from rawHtml (client-side, from scraper output). */
export const extractNavLinks = (rawHtml: string, baseUrl: string): RawNavLink[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(rawHtml, 'text/html');
  const origin = new URL(baseUrl).origin;

  return [...doc.querySelectorAll('nav a[href], header a[href]')]
    .map(a => ({
      url: (() => {
        try {
          return new URL(a.getAttribute('href') || '', baseUrl).href;
        } catch {
          return '';
        }
      })(),
      text: (a.textContent || '').trim(),
    }))
    .filter(link => {
      if (!link.url) {
        return false;
      }
      try {
        const parsed = new URL(link.url);
        return (
          parsed.origin === origin &&
          !parsed.hash &&
          !ASSET_EXT.test(parsed.pathname) &&
          link.text.length > 0 &&
          link.text.length < 50
        );
      } catch {
        return false;
      }
    });
};

// ── MERGE & DEDUPE ───────────────────────────────────────────────────

interface SitemapPage {
  url: string;
  path: string;
}

/**
 * Merge sitemap pages + nav links, deduplicate by pathname.
 * Nav link text takes priority for name (human-written).
 */
export const mergeDiscoveredPages = (
  sitemapPages: SitemapPage[],
  navLinks: RawNavLink[],
  baseUrl: string,
): DiscoveredPage[] => {
  const origin = new URL(baseUrl).origin;
  const byPath = new Map<string, DiscoveredPage>();

  // Nav links first (their text is human-written, higher quality names)
  for (const link of navLinks) {
    try {
      const parsed = new URL(link.url);
      const path = parsed.pathname.replace(/\/+$/, '') || '/';
      // Skip home page
      if (path === '/' || path === '/index' || path === '/index.html') {
        continue;
      }
      if (!byPath.has(path)) {
        byPath.set(path, {
          url: `${origin}${path}`,
          path,
          name: link.text || derivePageName(path),
          selected: true,
        });
      }
    } catch {
      // skip
    }
  }

  // Then sitemap pages (fill in anything nav links missed)
  for (const page of sitemapPages) {
    const path = page.path.replace(/\/+$/, '') || '/';
    if (path === '/' || path === '/index' || path === '/index.html') {
      continue;
    }
    if (!byPath.has(path)) {
      byPath.set(path, {
        url: page.url,
        path,
        name: derivePageName(path),
        selected: true,
      });
    }
  }

  // Sort by URL depth (shortest paths first), limit to 15
  return [...byPath.values()]
    .sort((a, b) => {
      const depthA = a.path.split('/').filter(Boolean).length;
      const depthB = b.path.split('/').filter(Boolean).length;
      return depthA - depthB;
    })
    .slice(0, 15);
};

// ── DISCOVER PAGES ───────────────────────────────────────────────────

/**
 * Discover pages from sitemap.xml + nav link extraction.
 * Returns merged, deduplicated DiscoveredPage array.
 */
export const discoverPages = async (
  baseUrl: string,
  rawHtml: string,
): Promise<DiscoveredPage[]> => {
  // 1. Fetch sitemap pages (catch errors → empty)
  let sitemapPages: SitemapPage[] = [];
  try {
    const res = await fetch('/api/sitemap', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: baseUrl }),
    });
    const data = await res.json();
    sitemapPages = data.pages || [];
  } catch {
    // Silently fall back
  }

  // 2. Extract nav links from rawHtml
  const navLinks = rawHtml ? extractNavLinks(rawHtml, baseUrl) : [];

  // 3. Merge & deduplicate
  return mergeDiscoveredPages(sitemapPages, navLinks, baseUrl);
};

// ── REFERENCE IMAGE HELPERS ──────────────────────────────────────────

/** Attach per-section reference images from site-structure extraction. */
export const attachReferenceImages = (
  sections: Section[],
  pageStructure: SiteStructureSection[],
): void => {
  for (const section of sections) {
    const matchingStructure = pageStructure.find(entry => entry.type === section.type);
    if (matchingStructure?.images?.length) {
      section.referenceImages = matchingStructure.images.map(image => ({
        url: image.url,
        alt: image.description || '',
        source: 'reference' as const,
      }));
    }
  }
};

/** Collect all page images into a flat list from structure data. */
export const collectPageImages = (
  pageStructure: SiteStructureSection[],
): Array<{ src: string; alt: string; category: string }> => {
  const images: Array<{ src: string; alt: string; category: string }> = [];
  for (const structSection of pageStructure) {
    for (const image of structSection.images ?? []) {
      if (image.url) {
        images.push({
          src: image.url,
          alt: image.description || '',
          category: structSection.type || 'other',
        });
      }
    }
  }
  return images;
};
